import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid2';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import LinearProgress, {
  linearProgressClasses,
} from '@mui/material/LinearProgress';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import ClearIcon from '@mui/icons-material/Clear';
import UploadIcon from '@mui/icons-material/Upload';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import PageviewIcon from '@mui/icons-material/Pageview';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import ChevronRightOutlinedIcon from '@mui/icons-material/ChevronRightOutlined';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import Divider from '@mui/material/Divider';
import { Header } from '../components';
import { useModal } from '../../../components/common';
import CreateTree from '../../family-tree/components/CreateTree';
import ImportGEDCOM from '../../family-tree/components/ImportGEDCOM';
import {
  useGetFamilyTree,
  useGetFamilyTreesByUser,
  useGetFamilyStories,
} from '../../../hooks/treeHooks';
import { useGetCulturalPostsByUser } from '../../../hooks/hubHooks';
import { useGetMigrationRecordsByTreeId } from '../../../hooks/recordHooks';
import { transformDate } from '../../../utils/date';
import CreateCulturalPost from '../../cultural-context/components/CreateEditPost';
import { useDeleteCulturalPost } from '../../../hooks/hubHooks';
import queryClient from '../../../core/http/react-query';
import { useAuth } from '../../../components/auth/AuthProvider';
import DeleteModal from '../../../components/common/DeleteModal';
import ErrorDisplay from '../../../components/common/ErrorDisplay';
import { maxFamilyMembers, maxFamilyTrees } from '../../../core';
import { AppConfig } from '../../../core';
import { ContextStatus } from '../../../types';
import useIsAdmin from '../../../hooks/useIsAdmin';
import { capitalise } from '../../../utils';
import useSubscription from '../../../hooks/useSubscription';
import SubscribeModal from '../../../components/common/SubscribeModal';

const data = [
  {
    id: 'family-tree',
    title: 'Family tree',
    description: 'Create your family tree to map relationships',
    btnTitle: 'Create a family tree',
    icon: <AccountTreeIcon color="primary" />,
  },
  {
    id: 'hub',
    title: 'Cultural hub',
    description:
      'Upload and share stories, traditions, and recipes unique to your heritage',
    btnTitle: 'Create a cultural record',
    icon: <AutoStoriesIcon color="primary" />,
  },
  {
    id: 'story',
    title: 'Family story',
    description:
      'Capture meaningful memories, traditions, and moments for your family tree',
    btnTitle: 'Add a story',
    icon: <HistoryEduIcon color="primary" />,
  },
] as const;

const completionMemberTarget = Math.min(maxFamilyMembers, 10);

type TreeNudgeAction = 'story' | 'journey';

type TreeNudge = {
  action: TreeNudgeAction;
  label: string;
  prompt: string;
  tab: 'stories' | 'journeys';
};

const normalizeArray = (payload: any): any[] => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const normalizeObject = (payload: any): any => {
  if (payload?.data) return payload.data;
  return payload;
};

const getCompletionPercent = (
  membersCount: number,
  storiesCount: number,
  journeysCount: number,
) => {
  const memberScore =
    completionMemberTarget > 0
      ? Math.min(1, membersCount / completionMemberTarget) * 30
      : 0;
  const storyScore = storiesCount > 0 ? 35 : 0;
  const journeyScore = journeysCount > 0 ? 35 : 0;
  return Math.min(100, Math.round(memberScore + storyScore + journeyScore));
};

const getTreeNudge = (
  treeName: string,
  storiesCount: number,
  journeysCount: number,
): TreeNudge | null => {
  if (storiesCount === 0) {
    return {
      action: 'story',
      label: 'Add story',
      prompt: `bring ${treeName} to life - add a story`,
      tab: 'stories',
    };
  }
  if (storiesCount > 0 && journeysCount === 0) {
    return {
      action: 'journey',
      label: 'Add journey',
      prompt: `map ${treeName} journeys - add a journey`,
      tab: 'journeys',
    };
  }
  return null;
};

type TreeListItemBaseProps = {
  tree: any;
  userId?: string;
  isSmallScreen: boolean;
  nudge?: TreeNudge | null;
  completionPercent?: number;
  membersCount?: number;
  storiesCount?: number;
  journeysCount?: number;
};

function TreeListItemBase({
  tree,
  userId,
  isSmallScreen,
  nudge,
  completionPercent,
  membersCount,
  storiesCount,
  journeysCount,
}: TreeListItemBaseProps) {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation('dashboard');

  const hasCounts =
    typeof membersCount === 'number' &&
    typeof storiesCount === 'number' &&
    typeof journeysCount === 'number';
  const showNudge = Boolean(nudge) && typeof completionPercent === 'number';
  const isCollaborator = userId && tree.created_by !== userId;

  return (
    <Box
      onClick={() => navigate(`/app/tree/${tree.id}`)}
      sx={{
        borderRadius: 3,
        border: '1px solid',
        borderColor: theme.palette.divider,
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'box-shadow 150ms ease, border-color 150ms ease',
        '&:hover': {
          borderColor: theme.palette.grey[400],
          boxShadow: theme.shadows[2],
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
          px: 2,
          pt: 2,
          pb: showNudge ? 1.5 : 2,
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
            <Typography
              component="h2"
              variant="subtitle1"
              fontWeight={700}
              noWrap
            >
              {tree.name}
            </Typography>
            {isCollaborator && (
              <Chip
                label="Collaborator"
                size="small"
                color="success"
                variant="outlined"
                sx={{ flexShrink: 0 }}
              />
            )}
          </Box>
          <Typography
            variant="caption"
            sx={{ color: theme.palette.text.secondary, display: 'block' }}
          >
            {hasCounts
              ? `${membersCount} members · ${storiesCount} stories · ${journeysCount} journeys`
              : `${t('dashboard:update')} ${transformDate(tree.updated_at)}`}
          </Typography>
        </Box>
        <Box
          sx={{
            color: theme.palette.text.secondary,
            display: 'flex',
            alignItems: 'center',
            flexShrink: 0,
          }}
        >
          <ChevronRightOutlinedIcon fontSize="small" />
        </Box>
      </Box>
      {showNudge && nudge && (
        <Box
          sx={{
            borderTop: '1px solid',
            borderColor: theme.palette.divider,
            px: 2,
            pt: 1.5,
            pb: 2,
            backgroundColor:
              theme.palette.mode === 'dark'
                ? theme.palette.grey[900]
                : theme.palette.grey[50],
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 0.75,
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: theme.palette.text.secondary }}
            >
              Tree completeness
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: theme.palette.text.secondary }}
            >
              {completionPercent}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={completionPercent}
            sx={{
              height: 4,
              borderRadius: 999,
              mb: 1.5,
              backgroundColor: theme.palette.divider,
              [`& .${linearProgressClasses.bar}`]: {
                borderRadius: 999,
                backgroundColor: theme.palette.primary.main,
              },
            }}
          />
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              flexWrap: isSmallScreen ? 'wrap' : 'nowrap',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                flexShrink: 1,
                minWidth: 0,
              }}
            >
              <Box
                component="span"
                sx={{ fontWeight: 700, color: theme.palette.text.primary }}
              >
                Next step:
              </Box>{' '}
              {nudge.prompt}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              fullWidth={isSmallScreen}
              sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
              onClick={(event) => {
                event.stopPropagation();
                navigate(`/app/tree/${tree.id}?tab=${nudge.tab}&action=create`);
              }}
            >
              {nudge.label}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
}

type OwnerTreeListItemProps = {
  tree: any;
  userId?: string;
  isSmallScreen: boolean;
};

function OwnerTreeListItem({
  tree,
  userId,
  isSmallScreen,
}: OwnerTreeListItemProps) {
  const {
    data: treeDetails,
    isLoading: isTreeLoading,
    isError: isTreeError,
  } = useGetFamilyTree(tree.id);
  const {
    data: storyData,
    isLoading: isStoriesLoading,
    isError: isStoriesError,
  } = useGetFamilyStories(tree.id);
  const {
    data: journeyData,
    isLoading: isJourneysLoading,
    isError: isJourneysError,
  } = useGetMigrationRecordsByTreeId(tree.id);

  const treeInfo = normalizeObject(treeDetails);
  const membersCount = treeInfo?.members?.length ?? 0;
  const storiesCount = normalizeArray(storyData).length;
  const journeysCount = normalizeArray(journeyData).length;
  const nudge = getTreeNudge(tree.name, storiesCount, journeysCount);

  const showNudge =
    !isTreeLoading &&
    !isStoriesLoading &&
    !isJourneysLoading &&
    !isTreeError &&
    !isStoriesError &&
    !isJourneysError;
  const completionPercent = getCompletionPercent(
    membersCount,
    storiesCount,
    journeysCount,
  );

  return (
    <TreeListItemBase
      tree={tree}
      userId={userId}
      isSmallScreen={isSmallScreen}
      nudge={showNudge ? nudge : null}
      completionPercent={showNudge ? completionPercent : undefined}
      membersCount={showNudge ? membersCount : undefined}
      storiesCount={showNudge ? storiesCount : undefined}
      journeysCount={showNudge ? journeysCount : undefined}
    />
  );
}

type CollaboratorTreeListItemProps = {
  tree: any;
  userId?: string;
  isSmallScreen: boolean;
};

function CollaboratorTreeListItem({
  tree,
  userId,
  isSmallScreen,
}: CollaboratorTreeListItemProps) {
  return (
    <TreeListItemBase
      tree={tree}
      userId={userId}
      isSmallScreen={isSmallScreen}
    />
  );
}

export function DashboardPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { openModal, closeModal } = useModal();
  const { t } = useTranslation('dashboard');
  const { enqueueSnackbar } = useSnackbar();
  const { currentUser } = useAuth();
  const { isAdmin, loading } = useIsAdmin(currentUser);

  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [itemToDelete, setItemToDelete] = useState<string>('');

  const userId = currentUser?.uid;

  const { isPremium } = useSubscription();

  const {
    data: familyTrees,
    isLoading,
    isError,
    error: treeError,
    refetch: treeRefetch,
  } = useGetFamilyTreesByUser(userId ?? '', currentUser?.email ?? '');
  const treeData = familyTrees as any;

  const {
    data: Posts,
    isLoading: isPostsLoading,
    isError: isPostsError,
    error: postsError,
    refetch: postsRefetch,
  } = useGetCulturalPostsByUser(userId ?? '');
  const postsData = Posts as any;

  const deleteMutation = useDeleteCulturalPost(itemToDelete);

  if (loading || isLoading || isPostsLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100dvh',
          mt: isSmallScreen ? '64px' : '',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }
  if (isError || isPostsError) {
    return (
      <ErrorDisplay
        error={treeError || postsError}
        onRetry={treeRefetch || postsRefetch}
      />
    );
  }

  function handleDeletePost() {
    deleteMutation.mutate(undefined, {
      onSuccess: () => {
        enqueueSnackbar('Cultural post deleted successfully!', {
          variant: 'success',
        });
        queryClient.refetchQueries({
          queryKey: ['culturalPosts', userId],
          exact: true,
        });
        closeModal?.();
      },
      onError: (error) => {
        enqueueSnackbar('Failed to delete cultural post', {
          variant: 'error',
        });
        console.error('Error deleting post:', error);
      },
    });
  }

  const isTreeLimitReached =
    treeData?.filter((tree: any) => tree.created_by === currentUser?.uid)
      ?.length >= maxFamilyTrees;

  const isPaidUser = isAdmin || isPremium;
  const ownedTrees = (treeData ?? []).filter(
    (tree: any) => tree.created_by === currentUser?.uid,
  );

  function handleAddStory() {
    openModal(
      <Box
        component="section"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '90vh',
          animation: 'treeFormFade 240ms cubic-bezier(0.2, 0, 0, 1)',
          '@keyframes treeFormFade': {
            '0%': { opacity: 0, transform: 'translateY(8px)' },
            '100%': { opacity: 1, transform: 'translateY(0)' },
          },
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            px: { xs: 3, md: 4 },
            pt: { xs: 3, md: 4 },
            pb: 2.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box>
            <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
              {ownedTrees.length
                ? 'Add a family story'
                : 'Start with a family tree'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {ownedTrees.length
                ? 'Choose which tree to add your story to.'
                : 'Create a family tree before adding your first story.'}
            </Typography>
          </Box>
          <IconButton
            onClick={closeModal}
            size="small"
            aria-label="Close"
            sx={{ mt: 0.5 }}
          >
            <ClearIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            px: { xs: 3, md: 4 },
            py: 3,
          }}
        >
          {ownedTrees.length > 0 ? (
            <List aria-label="Your family trees" disablePadding>
              {ownedTrees.map((tree: any) => (
                <ListItemButton
                  key={tree.id}
                  onClick={() => {
                    closeModal();
                    navigate(`/app/tree/${tree.id}?tab=stories&action=create`);
                  }}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    mb: 1,
                    transition:
                      'transform 220ms cubic-bezier(0.2, 0, 0, 1), box-shadow 220ms cubic-bezier(0.2, 0, 0, 1), border-color 220ms cubic-bezier(0.2, 0, 0, 1)',
                    '&:hover': {
                      transform: 'translateY(-1px)',
                      borderColor: 'primary.main',
                      boxShadow: 1,
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: 'primary.main' }}>
                    <AccountTreeIcon fontSize="small" aria-hidden="true" />
                  </ListItemIcon>
                  <ListItemText primary={tree.name} />
                  <ChevronRightOutlinedIcon aria-hidden="true" color="action" />
                </ListItemButton>
              ))}
            </List>
          ) : (
            <Box
              sx={{
                display: 'grid',
                justifyItems: 'center',
                py: 3,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
              }}
            >
              <Box
                sx={{
                  display: 'grid',
                  placeItems: 'center',
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  color: 'primary.main',
                  backgroundColor: 'action.hover',
                }}
                aria-hidden="true"
              >
                <AccountTreeIcon />
              </Box>
            </Box>
          )}
        </Box>

        <Box
          sx={{
            flexShrink: 0,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 2,
            px: { xs: 3, md: 4 },
            py: 2.5,
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Button
            variant="outlined"
            onClick={closeModal}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            Cancel
          </Button>
          {!ownedTrees.length && (
            <Button
              variant="contained"
              onClick={handleCreateTreeForStory}
              sx={{ textTransform: 'none', borderRadius: 2 }}
            >
              Create a family tree
            </Button>
          )}
        </Box>
      </Box>,
    );
  }

  function handleCreateTreeForStory() {
    closeModal();
    openModal(<CreateTree redirectToStory />);
  }

  return (
    <Box
      component="main"
      sx={{ flexGrow: 1, overflow: 'auto', width: '100%', height: '100dvh' }}
    >
      <Helmet>
        <title>Dashboard | {AppConfig.appName}</title>
        <meta name="description" content="View your dashboard and analytics" />
        <meta
          property="og:title"
          content={`${AppConfig.appName} - Dashboard`}
        />
        <meta
          property="og:description"
          content="View your personal dashboard and analytics"
        />
      </Helmet>
      <Stack
        spacing={2}
        sx={{
          alignItems: 'center',
          mx: 3,
          pb: 5,
          mt: { xs: 8, md: 0 },
        }}
      >
        <Header
          title={t('dashboard:home')}
          headerName={t('dashboard:dashboard')}
        />
        <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '1700px' } }}>
          {currentUser?.displayName && (
            <Typography variant="h2" pt={2} pb={3} fontWeight={700}>
              Hi {capitalise(currentUser?.displayName)},
            </Typography>
          )}
          <Typography component="h2" variant="h6" sx={{ mb: 2 }}>
            {t('dashboard:labels.overview')}
          </Typography>
          <Grid
            container
            spacing={3}
            columns={12}
            sx={{ mb: (theme) => theme.spacing(2) }}
          >
            {data
              .filter((item) => isAdmin || item.id !== 'hub')
              .map((data, index) => (
                <Grid
                  key={`dashboard_${index}`}
                  size={{ xs: 12, sm: 6, lg: 3 }}
                >
                  <Card
                    variant="outlined"
                    sx={{ height: '100%', flexGrow: 1, borderRadius: 4 }}
                  >
                    <CardContent>
                      {data?.icon}
                      <Typography
                        component="h2"
                        variant="subtitle2"
                        gutterBottom
                        sx={{ fontWeight: '600' }}
                      >
                        {data?.title}
                      </Typography>
                      <Typography sx={{ color: 'text.secondary', mb: '8px' }}>
                        {data?.description}
                      </Typography>
                      <Button
                        variant="contained"
                        size="small"
                        color="primary"
                        endIcon={<ChevronRightRoundedIcon />}
                        fullWidth={isSmallScreen}
                        onClick={() => {
                          if (
                            isTreeLimitReached &&
                            !isPaidUser &&
                            data.id === 'family-tree'
                          ) {
                            return openModal(
                              <SubscribeModal
                                closeModal={closeModal}
                                handleClose={closeModal}
                                message={`You can only add up to ${maxFamilyTrees} on the Free plan.`}
                                title="Expand Your Ancestry"
                              />,
                            );
                          }
                          switch (data.id) {
                            case 'family-tree':
                              openModal(<CreateTree />);
                              break;
                            case 'hub':
                              openModal(<CreateCulturalPost />);
                              break;
                            case 'story':
                              handleAddStory();
                              break;
                            default:
                              break;
                          }
                        }}
                      >
                        {data?.btnTitle}
                      </Button>
                      {data?.id === 'family-tree' && (
                        <Box mt={1}>
                          <Divider sx={{ mb: 1 }} />
                          <Button
                            variant="text"
                            size="small"
                            color="primary"
                            startIcon={<UploadIcon />}
                            fullWidth={isSmallScreen}
                            onClick={() => {
                              if (!isPaidUser) {
                                return openModal(
                                  <SubscribeModal
                                    closeModal={closeModal}
                                    handleClose={closeModal}
                                    message={
                                      'GEDCOM feature is unavailable on the Free plan.'
                                    }
                                    title="Upload Your Family Tree GEDCOM data"
                                  />,
                                );
                              }
                              openModal(
                                <ImportGEDCOM closeModal={closeModal} />,
                              );
                            }}
                          >
                            Import from a GEDCOM file
                          </Button>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
          </Grid>

          <Divider sx={{ my: 4 }} />

          <Typography component="h2" variant="h6" sx={{ my: 2 }}>
            {t('dashboard:labels.details')}
          </Typography>
          <Grid container spacing={2} columns={12}>
            <Grid size={{ xs: 12 }}>
              <Card
                variant="outlined"
                sx={{ width: '100%', minHeight: 400, borderRadius: 4 }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography component="h2" variant="subtitle1" gutterBottom>
                    {t('dashboard:labels.familyTrees')}
                    {treeData?.length > 0 && ` (${treeData.length})`}
                  </Typography>
                  <Grid container spacing={2} columns={12}>
                    {treeData &&
                      [...treeData]
                        .sort(
                          (a: any, b: any) =>
                            new Date(a.created_at).getTime() -
                            new Date(b.created_at).getTime(),
                        )
                        .map((tree: any) => {
                          const isOwner = tree.created_by === currentUser?.uid;
                          return (
                            <Grid key={tree.id} size={{ xs: 12, sm: 6, lg: 4 }}>
                              {isOwner ? (
                                <OwnerTreeListItem
                                  tree={tree}
                                  userId={userId}
                                  isSmallScreen={isSmallScreen}
                                />
                              ) : (
                                <CollaboratorTreeListItem
                                  tree={tree}
                                  userId={userId}
                                  isSmallScreen={isSmallScreen}
                                />
                              )}
                            </Grid>
                          );
                        })}
                    {!treeData?.length && (
                      <Grid size={{ xs: 12 }}>
                        <Typography variant="caption">
                          {t('dashboard:noFamilyAvailable')}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          {isAdmin && (
            <>
              <Divider sx={{ my: 4 }} />
              <Grid container spacing={2} columns={12}>
                <Grid size={{ xs: 12 }}>
                  <Card
                    variant="outlined"
                    sx={{ width: '100%', minHeight: 400, borderRadius: 4 }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Typography
                        component="h2"
                        variant="subtitle1"
                        gutterBottom
                      >
                        {t('dashboard:labels.cultural')}
                        {postsData?.length > 0 && ` (${postsData.length})`}
                      </Typography>
                      <Box sx={{ height: 300, overflowY: 'scroll' }}>
                        <Stack
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            gap: 1,
                          }}
                        >
                          {postsData &&
                            postsData.map((post: any) => {
                              return (
                                <Stack
                                  key={post.id}
                                  direction="row"
                                  sx={{
                                    alignContent: {
                                      xs: 'center',
                                      sm: 'flex-start',
                                    },
                                    alignItems: 'center',
                                    gap: 1,
                                    justifyContent: 'space-between',
                                    p: 1,
                                  }}
                                >
                                  <Box>
                                    <Typography
                                      component="h2"
                                      variant="subtitle2"
                                    >
                                      {post.title}
                                    </Typography>
                                    <Box
                                      display="flex"
                                      alignItems="center"
                                      justifyContent="flex-start"
                                      gap={1}
                                    >
                                      <Typography
                                        component="p"
                                        variant="caption"
                                        sx={{
                                          color: theme.palette.text.secondary,
                                        }}
                                      >
                                        {t('dashboard:update')}{' '}
                                        {transformDate(post.updated_at)}
                                      </Typography>
                                      <Chip
                                        label={post.context_status}
                                        size="small"
                                        color={
                                          post.context_status ===
                                          ContextStatus.APPROVED
                                            ? 'success'
                                            : post.context_status ===
                                                ContextStatus.REJECTED
                                              ? 'error'
                                              : 'info'
                                        }
                                      />
                                    </Box>
                                  </Box>
                                  <Box>
                                    {post.context_status !==
                                    ContextStatus.APPROVED ? (
                                      <>
                                        <Tooltip title="Edit">
                                          <IconButton
                                            onClick={() =>
                                              openModal(
                                                <CreateCulturalPost
                                                  post={post}
                                                />,
                                              )
                                            }
                                          >
                                            <EditIcon />
                                          </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete">
                                          <IconButton
                                            onClick={() => {
                                              setItemToDelete(post.id);
                                              openModal(
                                                <DeleteModal
                                                  closeModal={closeModal}
                                                  onDelete={handleDeletePost}
                                                  deleteMessage={`Are you sure you want to delete post: ${post.title}?`}
                                                  deleteTitle="Delete cultural post"
                                                />,
                                              );
                                            }}
                                          >
                                            <DeleteIcon />
                                          </IconButton>
                                        </Tooltip>
                                      </>
                                    ) : (
                                      <Tooltip title="View cultural post">
                                        <IconButton
                                          onClick={() =>
                                            navigate(`/hub/${post.id}`)
                                          }
                                        >
                                          <PageviewIcon />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                  </Box>
                                </Stack>
                              );
                            })}
                          {!postsData.length && (
                            <Typography variant="caption">
                              {t('dashboard:noCulturalStoryAvailable')}
                            </Typography>
                          )}
                        </Stack>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </>
          )}
        </Box>
      </Stack>
    </Box>
  );
}
